/**
 * Hook for managing competitors data in the Knowledge Graph widget.
 *
 * This hook fetches and organizes competitor data from multiple sources:
 * - Company competitors with themes (from ontology API)
 * - Screener data (overview + performance metrics)
 * - Entity details for each competitor
 *
 * Data mapping process:
 * - Screener data is positionally mapped to entities (entities[0] → data[0])
 * - Data is combined by entity tag for both table and drawer access
 *
 * The data is organized into:
 * - tableData: Array of competitor rows for the table display
 * - competitorsByTag: Map of competitor data keyed by entity tag for drawer access
 *
 * @param entityTag - The entity tag to fetch competitors for
 * @returns Object containing table data, competitor map, loading states, and metadata
 */

import { Entity } from '@api/api/entity-schema';
import { CompanyCompetitorsWithThemesResponse } from '@api/api/theme-overview';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useCompanyCompetitorWithThemes } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '@terminal/routes/query-params';
import {
  extractPerfKGData,
  extractScreenerOverviewData,
} from '@terminal/views/theme-overview/tab-content/utils';
import { createPostFilterQueryOptions } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/entity-screener-service/postFilterQueryOptions';
import { useMemo } from 'react';

const PAGE_SIZE = 200;

export type CompetitorData = {
  entity: Entity;
  competitorData: CompanyCompetitorsWithThemesResponse[0];
  competingThemes: CompanyCompetitorsWithThemesResponse[0]['competing_themes'];
};

export type CompetitorsByTag = Record<string, CompetitorData>;

export type CompetitorsTableData = {
  entity?: Entity;
  hasData: boolean;
  competingThemes: CompanyCompetitorsWithThemesResponse[0]['competing_themes'];
  rank: number;
} & Partial<Record<string, unknown>>;

export const useCompetitorsData = ({
  entityTag,
  enabled = true,
}: {
  entityTag: string;
  enabled?: boolean;
}) => {
  const { params } = useQueryParams();
  const selectedCompetitor = params[queryKeys.competitorTag];

  const {
    data: competitorsData,
    error: competitorsError,
    isFetching: isCompetitorsFetching,
  } = useCompanyCompetitorWithThemes({
    entityTag,
    enabled,
  });

  const entityTags = competitorsData
    ? competitorsData.map(item => item.company)
    : [];

  const requestFilters = {
    entity_tag: {
      values: [{ tags: entityTags }],
    },
  };

  const {
    data: screenerData,
    isFetching: isScreenerFetching,
    error: screenerError,
  } = useInfiniteQuery({
    ...createPostFilterQueryOptions({
      requestFilters,
      pageSize: PAGE_SIZE,
      staleTime: getTimeTillEndOfDayMs(),
      enabled: enabled && !!entityTags.length,
    }),
  });

  const entities = screenerData?.pages?.[0]?.entities;
  const latestData = screenerData?.pages?.[0];

  const { data: entitiesData, isLoading: isEntitiesLoading } = useEntities({
    entityTags: entities ?? [],
    appendFetching: true,
    enabled: enabled && !isScreenerFetching && !screenerError,
  });

  const { columnsMeta, combinedScreenerData } = useMemo(() => {
    const screenerOverview = extractScreenerOverviewData(latestData?.data);
    const screenerPerf = extractPerfKGData(latestData?.data);

    const combinedScreenerData = [
      ...screenerOverview.data,
      ...screenerPerf.data,
    ];
    const columnsMeta = [...screenerOverview.meta, ...screenerPerf.meta];

    return { columnsMeta, combinedScreenerData };
  }, [latestData]);

  const screenerByTag = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    entities?.forEach((entityTag, index) => {
      map.set(entityTag, combinedScreenerData[index] || {});
    });
    return map;
  }, [entities, combinedScreenerData]);

  const organisedData = useMemo(() => {
    if (!competitorsData || !combinedScreenerData || !entitiesData) {
      return null;
    }

    const competitorsByTag: CompetitorsByTag = {};
    const tableData: CompetitorsTableData[] = [];

    competitorsData.forEach(competitor => {
      const entity = entitiesData.find(e => e.tag === competitor.company);

      if (entity) {
        competitorsByTag[competitor.company] = {
          entity,
          competitorData: competitor,
          competingThemes: competitor.competing_themes,
        };

        tableData.push({
          entity,
          hasData: !isEntitiesLoading && !!entity,
          competingThemes: competitor.competing_themes,
          rank: competitor.relationship.rank,
          ...(screenerByTag.get(competitor.company) || {}),
        });
      }
    });

    return {
      tableData,
      columnsMeta,
      competitorsByTag,
      selectedCompetitor,
    };
  }, [
    competitorsData,
    entitiesData,
    combinedScreenerData,
    columnsMeta,
    selectedCompetitor,
  ]);

  return {
    tableData: organisedData?.tableData ?? [],
    competitorsByTag: organisedData?.competitorsByTag ?? {},
    selectedCompetitor,
    columnsMeta,
    error: competitorsError || screenerError,
    isEmpty: !isCompetitorsFetching && !competitorsData?.length,
    isFetching:
      isCompetitorsFetching || isScreenerFetching || isEntitiesLoading,
  };
};
